import {
  initialize,
  getFlag,
  isLoaded,
} from '@pedidosya/web-native-bridges/fwf';
import * as React from 'react';
import PropTypes from 'prop-types';
import { getEnvironment } from '@commons/config';

const FLAGS = {
  WEB_TO_NATIVE_CHECKOUT: 'web-to-native-checkout',
};

const flags = [FLAGS.WEB_TO_NATIVE_CHECKOUT];

const TOKENS = {
  local: 'f5c12069-ea27-460d-9108-40b72cd1e620',
  stg: '8c3b1a80-7faf-4e05-86f1-d18b4f5b8449',
  live: 'e25a4142-87ea-4ba4-aa10-8158a6dc9f4a',
};

const getFwfConfig = (env, flagKeys) => {
  const token = TOKENS[env];
  return {
    url: 'https://us-client.fwf.deliveryhero.net',
    debugMode: env !== 'live',
    log: env !== 'live',
    token,
    featureKeys: flagKeys,
  };
};

const FunWithFlagsContext = React.createContext({
  flags: [],
});

FunWithFlagsContext.displayName = 'FunWithFlagsContext';

async function initFunWithFlags(env, flagKeys) {
  try {
    const config = getFwfConfig(env, flagKeys);
    await initialize({
      config,
      context: {
        userId: 'user-id',
      },
    });
    return isLoaded();
  } catch (error) {
    console.error('[FWF][INIT]', error);
    return false;
  }
}

function FunWithFlagsProvider({ children }) {
  const [flagsMap, setFlagsMap] = React.useState([]);

  const environment = getEnvironment();

  React.useEffect(() => {
    const run = async () => {
      await initFunWithFlags(environment, flags);
      const flagValues = await Promise.all(
        flags.map(flagName => getFlag(flagName)),
      );
      setFlagsMap(flagValues);
    };
    run();
  }, [environment]);

  const value = React.useMemo(() => ({ flags: flagsMap }), [flagsMap]);


  return (
    <FunWithFlagsContext.Provider value={value}>
      {children}
    </FunWithFlagsContext.Provider>
  );
}

FunWithFlagsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useFlags() {
  const context = React.useContext(FunWithFlagsContext);
  if (context === undefined) {
    throw new Error('useFlags must be used within an FunWithFlagsProvider');
  }
  return context.flags || [];
}

function useFlag(name, fallback) {
  const flagsValues = useFlags();
  const flagToCheck = flagsValues?.find(flag => flag?.keyName === name);

  return flagToCheck ?? { variation: fallback };
}

function useFlagVariation(name, fallback) {
  const flag = useFlag(name, fallback);

  return flag.isEnabled || flag.variation;
}

export { FunWithFlagsProvider, useFlagVariation, FLAGS };
